import { useUpload } from '../utils/uploadContext';
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  MdCloudUpload, MdVideoLibrary, MdCheck, MdArrowBack,
  MdArrowForward, MdAutoAwesome, MdClose, MdDownload,
  MdImage, MdAdd, MdEdit, MdSmartToy
} from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getChannels, uploadVideo } from '../utils/api';

const platformConfig = [
  {
    id: 'youtube', label: 'YouTube Long Form',
    icon: <FaYoutube style={{ fontSize: '32px', color: '#ff0000' }} />,
    color: '#ff0000', bg: 'rgba(255,0,0,0.08)', border: 'rgba(255,0,0,0.3)',
    description: 'Long form videos\nNo length limit'
  },
  {
    id: 'youtube_shorts', label: 'YouTube Shorts',
    icon: <FaYoutube style={{ fontSize: '32px', color: '#ff4444' }} />,
    color: '#ff4444', bg: 'rgba(255,68,68,0.08)', border: 'rgba(255,68,68,0.3)',
    description: 'Max 60 seconds\nVertical 9:16 format'
  },
  {
    id: 'facebook', label: 'Facebook',
    icon: <FaFacebook style={{ fontSize: '32px', color: '#1877f2' }} />,
    color: '#1877f2', bg: 'rgba(24,119,242,0.08)', border: 'rgba(24,119,242,0.3)',
    description: 'Upload to\nFacebook pages'
  },
  {
    id: 'tiktok', label: 'TikTok',
    icon: <FaTiktok style={{ fontSize: '32px', color: '#e0e0e0' }} />,
    color: '#e0e0e0', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.15)',
    description: 'Upload to\nTikTok account'
  },
  {
    id: 'instagram', label: 'Instagram Reels',
    icon: <FaInstagram style={{ fontSize: '32px', color: '#e1306c' }} />,
    color: '#e1306c', bg: 'rgba(225,48,108,0.08)', border: 'rgba(225,48,108,0.3)',
    description: 'Upload\nInstagram Reels'
  }
];

const emptyPlatformData = {
  title: '', description: '', keywords: [], hashtags: []
};

const emptyForm = {
  youtube: { ...emptyPlatformData },
  youtube_shorts: { ...emptyPlatformData },
  facebook: { ...emptyPlatformData },
  tiktok: { ...emptyPlatformData },
  instagram: { ...emptyPlatformData }
};

const PlatformTile = ({ platform, selected, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '12px', padding: '24px 16px',
        borderRadius: '16px', cursor: 'pointer', position: 'relative',
        background: selected ? platform.bg : hovered ? 'var(--bg-card)' : 'var(--bg-dark)',
        border: '2px solid ' + (selected ? platform.color : hovered ? platform.border : 'var(--border)'),
        transform: pressed ? 'scale(0.96)' : hovered ? 'scale(1.04) translateY(-3px)' : 'scale(1)',
        transition: 'all 0.2s ease',
        boxShadow: selected ? `0 8px 24px ${platform.bg}` :
          hovered ? `0 6px 16px ${platform.bg}` : 'none',
        minHeight: '140px'
      }}>
      {selected && (
        <div style={{
          position: 'absolute', top: '8px', right: '8px',
          width: '22px', height: '22px', borderRadius: '50%',
          background: platform.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 8px ${platform.color}`
        }}>
          <MdCheck style={{ color: 'white', fontSize: '14px' }} />
        </div>
      )}
      {platform.icon}
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontSize: '13px', fontWeight: '800',
          color: selected ? platform.color : 'var(--text-primary)',
          marginBottom: '4px', transition: 'all 0.2s ease'
        }}>
          {platform.label}
        </div>
        <div style={{
          fontSize: '11px', color: 'var(--text-secondary)',
          lineHeight: '1.4', whiteSpace: 'pre-line'
        }}>
          {platform.description}
        </div>
      </div>
    </div>
  );
};

const ActionBtn = ({ onClick, disabled, className, style, children }) => {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button className={className} onClick={onClick} disabled={disabled}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{
        ...style,
        transform: pressed ? 'scale(0.97)' : hovered && !disabled ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.15s ease',
        boxShadow: hovered && !disabled ? '0 6px 20px rgba(0,200,83,0.25)' : 'none'
      }}>
      {children}
    </button>
  );
};

const TagInput = ({ tags, onAdd, onRemove, placeholder, color = 'var(--primary)', bg = 'rgba(0,200,83,0.1)', border = 'rgba(0,200,83,0.3)' }) => {
  const [input, setInput] = useState('');
  const handleAdd = () => {
    let val = input.trim();
    if (!val) return;
    if (placeholder.includes('#') && !val.startsWith('#')) val = '#' + val;
    if (!tags.includes(val)) onAdd(val);
    setInput('');
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input className="form-control"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
          style={{ fontSize: '12px' }} />
        <button className="btn btn-secondary btn-sm" onClick={handleAdd}>
          <MdAdd />
        </button>
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {tags.map(tag => (
            <span key={tag} onClick={() => onRemove(tag)} style={{
              padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
              fontWeight: '600', background: bg, color: color,
              border: '1px solid ' + border,
              display: 'flex', alignItems: 'center', gap: '4px',
              cursor: 'pointer', transition: 'all 0.2s ease'
            }}>
              {tag} <MdClose style={{ fontSize: '11px' }} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const VideoUpload = () => {
  const [step, setStep] = useState(1);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const thumbnailInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [publishMode, setPublishMode] = useState('manual');
  const [metadataControl, setMetadataControl] = useState('user_choice');
  const [aiOptions, setAiOptions] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [detectedNiche, setDetectedNiche] = useState('');
  const [activePlatformTab, setActivePlatformTab] = useState(null);
  const [thumbnailConcepts, setThumbnailConcepts] = useState(null);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const [generatedThumbnailUrl, setGeneratedThumbnailUrl] = useState(null);
  const [inputMode, setInputMode] = useState('manual');
  const fileInputRef = useRef(null);
  const { startUpload, updateProgress, finishUpload, cancelUpload, setXhr, savedUploadState, setSavedUploadState } = useUpload();

  useEffect(() => {
    fetchChannels();
    if (savedUploadState) {
      setStep(savedUploadState.step || 1);
      setSelectedPlatforms(savedUploadState.selectedPlatforms || []);
      setVideoFile(savedUploadState.videoFile || null);
      setForm(savedUploadState.form || emptyForm);
      setSelectedChannels(savedUploadState.selectedChannels || []);
      setPublishMode(savedUploadState.publishMode || 'manual');
      setActivePlatformTab(savedUploadState.activePlatformTab || null);
    }
  }, []);
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (saving) {
        e.preventDefault();
        e.returnValue = 'Video is still uploading. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saving]);
  useEffect(() => {
    if (selectedPlatforms.length > 0 && !activePlatformTab) {
      setActivePlatformTab(selectedPlatforms[0]);
    }
  }, [selectedPlatforms]);

  const fetchChannels = async () => {
    try {
      const res = await getChannels();
      setChannels(res.data.data || []);
    } catch (error) {
      toast.error('Failed to load channels');
    }
  };

  const handleFileSelect = (file) => {
    if (!file || !file.type.startsWith('video/')) {
      toast.error('Please upload a valid video file');
      return;
    }
    setVideoFile(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      setVideoDuration(video.duration);
      if (selectedPlatforms.includes('youtube_shorts') && video.duration > 60) {
        toast.error(`Video is ${Math.round(video.duration)}s — exceeds YouTube Shorts 60 second limit`);
      }
    };
    video.src = URL.createObjectURL(file);
  };

  const togglePlatform = (id) => {
    setSelectedPlatforms(prev => {
      const updated = prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id];
      if (!activePlatformTab && updated.length > 0) setActivePlatformTab(updated[0]);
      if (activePlatformTab === id && updated.length > 0) setActivePlatformTab(updated[0]);
      return updated;
    });
    setSelectedChannels([]);
  };

  const toggleChannel = (channelId) => {
    const id = channelId.toString();
    setSelectedChannels(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const updatePlatformField = (platform, field, value) => {
    setForm(prev => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value }
    }));
  };

  const addTag = (platform, field, value) => {
    setForm(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: [...(prev[platform][field] || []), value]
      }
    }));
  };

  const removeTag = (platform, field, value) => {
    setForm(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: (prev[platform][field] || []).filter(t => t !== value)
      }
    }));
  };

  const detectNiche = async (apiKey, title) => {
    try {
      const res = await fetch('https://vantage-production-4475.up.railway.app/api/obsidian/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          
          
        },
        body: JSON.stringify({
          model: 'claude-opus-4-5',
          max_tokens: 30,
          system: 'Detect content niche from video title. Respond with ONLY 1-3 words.',
          messages: [{ role: 'user', content: `Niche for: ${title}` }]
        })
      });
      const data = await res.json();
      return data.content[0].text.trim();
    } catch (e) { return 'General'; }
  };

  const generateAIOptions = async () => {
    
    

    const anyTitle = Object.values(form).find(p => p.title)?.title || videoFile?.name || 'Untitled Video';
    const anyDesc = Object.values(form).find(p => p.description)?.description || '';
    const userKeywords = Object.values(form).flatMap(p => p.keywords || []).filter(Boolean).join(', ');
    const userHashtags = Object.values(form).flatMap(p => p.hashtags || []).filter(Boolean).join(' ');

    setGeneratingAI(true);
    try {
      const niche = await detectNiche(apiKey, anyTitle);
      setDetectedNiche(niche);

      const platformList = selectedPlatforms.map(p => {
        const cfg = platformConfig.find(pc => pc.id === p);
        return cfg?.label || p;
      }).join(', ');

      const response = await fetch('https://vantage-production-4475.up.railway.app/api/obsidian/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          
          
        },
        body: JSON.stringify({
          model: 'claude-opus-4-5',
          max_tokens: 8000,
          system: `You are Obsidian AI, an elite content metadata specialist. You deeply understand how each platform algorithm works differently:

YOUTUBE algorithm: Rewards SEO optimised titles, keyword rich descriptions with timestamps, 5-10 relevant hashtags, long tail keywords
YOUTUBE SHORTS algorithm: Rewards punchy short titles, minimal description, trending hashtags, viral keywords  
TIKTOK algorithm: Rewards trendy titles, short captions, For You Page hashtags, viral sound references, trending keywords
FACEBOOK algorithm: Rewards emotional shareability titles, community story descriptions, minimal hashtags, broad keywords
INSTAGRAM algorithm: Rewards aesthetic titles, storytelling captions with line breaks, 20-30 niche hashtags, visual keywords

Each platform gets COMPLETELY DIFFERENT optimised content. Never use the same title or description across platforms.
You ONLY help with content metadata for YouTube, Facebook, TikTok and Instagram.`,
          messages: [{
            role: 'user',
            content: `Generate 5 complete title and description options for this video. Each option has a unique creative angle.

VIDEO TITLE HINT: ${anyTitle}
DESCRIPTION HINT: ${anyDesc.substring(0, 200) || 'Not provided'}
DETECTED NICHE: ${niche}
SELECTED PLATFORMS: ${platformList}
USER KEYWORDS TO INCORPORATE: ${userKeywords || 'None — generate the best ones for this niche'}
USER HASHTAGS TO INCORPORATE: ${userHashtags || 'None — generate the best ones per platform'}

For EACH of the 5 options generate platform specific content for EVERY selected platform.

${selectedPlatforms.includes('youtube') || selectedPlatforms.includes('youtube_shorts') ? `
YOUTUBE REQUIREMENTS per option:
- YOUTUBE_TITLE: SEO optimised, under 60 chars, curiosity or benefit driven
- YOUTUBE_DESCRIPTION: Full professional description — hook sentence, 3-5 bullet points what video covers, timestamps placeholder, subscribe CTA, links placeholder, 5-10 niche hashtags at bottom
- YOUTUBE_KEYWORDS: 8-10 high volume low competition SEO keywords as comma separated list
- YOUTUBE_HASHTAGS: 5-10 YouTube specific hashtags` : ''}

${selectedPlatforms.includes('tiktok') ? `
TIKTOK REQUIREMENTS per option:
- TIKTOK_TITLE: Punchy, trendy, under 40 chars, viral energy
- TIKTOK_CAPTION: Under 150 chars, hook style, conversation starter
- TIKTOK_KEYWORDS: 5-8 trending TikTok search keywords
- TIKTOK_HASHTAGS: 5-8 For You Page optimised hashtags` : ''}

${selectedPlatforms.includes('facebook') ? `
FACEBOOK REQUIREMENTS per option:
- FACEBOOK_TITLE: Emotional, curiosity driven, shareable, under 80 chars
- FACEBOOK_DESCRIPTION: 2-3 paragraphs, storytelling style, community angle, share trigger at end, CTA
- FACEBOOK_KEYWORDS: 5-8 Facebook search optimised keywords
- FACEBOOK_HASHTAGS: 3-5 Facebook hashtags (less is more on Facebook)` : ''}

${selectedPlatforms.includes('instagram') ? `
INSTAGRAM REQUIREMENTS per option:
- INSTAGRAM_TITLE: Aesthetic, punchy, Reels optimised, under 50 chars
- INSTAGRAM_CAPTION: 3-5 lines storytelling, line breaks, emotional hook, CTA like save or share or comment, 20-30 niche hashtags at bottom
- INSTAGRAM_KEYWORDS: 8-10 Instagram search keywords
- INSTAGRAM_HASHTAGS: 20-30 niche specific Instagram hashtags` : ''}

Format EXACTLY like this for all 5 options:

=== OPTION 1 ===
YOUTUBE_TITLE: [title]
YOUTUBE_DESCRIPTION:
[full description]
YOUTUBE_KEYWORDS: [kw1, kw2, kw3...]
YOUTUBE_HASHTAGS: [#tag1 #tag2 #tag3...]
TIKTOK_TITLE: [title]
TIKTOK_CAPTION: [caption]
TIKTOK_KEYWORDS: [kw1, kw2...]
TIKTOK_HASHTAGS: [#tag1 #tag2...]
FACEBOOK_TITLE: [title]
FACEBOOK_DESCRIPTION:
[full description]
FACEBOOK_KEYWORDS: [kw1, kw2...]
FACEBOOK_HASHTAGS: [#tag1 #tag2...]
INSTAGRAM_TITLE: [title]
INSTAGRAM_CAPTION:
[full caption with hashtags at bottom]
INSTAGRAM_KEYWORDS: [kw1, kw2...]
INSTAGRAM_HASHTAGS: [#tag1 #tag2 #tag3...]
CTR_SCORE: X/10
SEO_SCORE: X/10
BEST_FOR: [niche type this angle works best for]

=== OPTION 2 ===
[same format]

=== OPTION 3 ===
[same format]

=== OPTION 4 ===
[same format]

=== OPTION 5 ===
[same format]

AUTO_SELECTED: [1-5]
AUTO_SELECTION_REASON: [detailed reason why this option performs best for this niche across all selected platforms]`
          }]
        })
      });

      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const rawText = data.content[0].text;

      const options = [];
      const optionBlocks = rawText.split(/=== OPTION \d+ ===/);
      optionBlocks.shift();

      optionBlocks.forEach(block => {
        const extract = (key, multiline = false) => {
          if (multiline) {
            const match = block.match(new RegExp(key + ':\\n([\\s\\S]+?)(?=\\n[A-Z_]+:|$)'));
            return match ? match[1].trim() : '';
          }
          const match = block.match(new RegExp(key + ':\\s*(.+?)(?:\\n|$)'));
          return match ? match[1].trim() : '';
        };

        const parseList = (str) => str ? str.split(',').map(s => s.trim()).filter(Boolean) : [];
        const parseHashtags = (str) => str ? str.split(/\s+/).map(s => s.trim()).filter(s => s.startsWith('#') || s) : [];

        options.push({
          youtube_title: extract('YOUTUBE_TITLE'),
          youtube_description: extract('YOUTUBE_DESCRIPTION', true),
          youtube_keywords: parseList(extract('YOUTUBE_KEYWORDS')),
          youtube_hashtags: parseHashtags(extract('YOUTUBE_HASHTAGS')),
          tiktok_title: extract('TIKTOK_TITLE'),
          tiktok_caption: extract('TIKTOK_CAPTION'),
          tiktok_keywords: parseList(extract('TIKTOK_KEYWORDS')),
          tiktok_hashtags: parseHashtags(extract('TIKTOK_HASHTAGS')),
          facebook_title: extract('FACEBOOK_TITLE'),
          facebook_description: extract('FACEBOOK_DESCRIPTION', true),
          facebook_keywords: parseList(extract('FACEBOOK_KEYWORDS')),
          facebook_hashtags: parseHashtags(extract('FACEBOOK_HASHTAGS')),
          instagram_title: extract('INSTAGRAM_TITLE'),
          instagram_caption: extract('INSTAGRAM_CAPTION', true),
          instagram_keywords: parseList(extract('INSTAGRAM_KEYWORDS')),
          instagram_hashtags: parseHashtags(extract('INSTAGRAM_HASHTAGS')),
          ctr_score: extract('CTR_SCORE'),
          seo_score: extract('SEO_SCORE'),
          best_for: extract('BEST_FOR')
        });
      });

      const autoMatch = rawText.match(/AUTO_SELECTED:\s*(\d+)/);
      const reasonMatch = rawText.match(/AUTO_SELECTION_REASON:\s*([\s\S]+?)(?:\n===|$)/);
      const autoSelected = autoMatch ? parseInt(autoMatch[1]) : 1;
      const autoReason = reasonMatch ? reasonMatch[1].trim() : '';

      setAiOptions({ options, auto_selected: autoSelected, auto_selection_reason: autoReason });
      if (metadataControl === 'obsidian_decides') {
        applyOption(autoSelected - 1, options);
      }
      toast.success(`Niche: ${niche} — 5 platform specific options generated!`);
    } catch (error) {
      toast.error('Failed: ' + error.message);
    } finally {
      setGeneratingAI(false);
    }
  };

  const applyOption = (index, options) => {
    const opts = options || aiOptions?.options;
    if (!opts) return;
    const option = opts[index];
    if (!option) return;

    setSelectedOption(index);
    const newForm = { ...form };

    if (selectedPlatforms.includes('youtube') || selectedPlatforms.includes('youtube_shorts')) {
      newForm.youtube = {
        title: option.youtube_title || form.youtube.title,
        description: option.youtube_description || form.youtube.description,
        keywords: option.youtube_keywords?.length > 0 ? option.youtube_keywords : form.youtube.keywords,
        hashtags: option.youtube_hashtags?.length > 0 ? option.youtube_hashtags : form.youtube.hashtags
      };
      if (selectedPlatforms.includes('youtube_shorts')) {
        newForm.youtube_shorts = { ...newForm.youtube };
      }
    }
    if (selectedPlatforms.includes('tiktok')) {
      newForm.tiktok = {
        title: option.tiktok_title || form.tiktok.title,
        description: option.tiktok_caption || form.tiktok.description,
        keywords: option.tiktok_keywords?.length > 0 ? option.tiktok_keywords : form.tiktok.keywords,
        hashtags: option.tiktok_hashtags?.length > 0 ? option.tiktok_hashtags : form.tiktok.hashtags
      };
    }
    if (selectedPlatforms.includes('facebook')) {
      newForm.facebook = {
        title: option.facebook_title || form.facebook.title,
        description: option.facebook_description || form.facebook.description,
        keywords: option.facebook_keywords?.length > 0 ? option.facebook_keywords : form.facebook.keywords,
        hashtags: option.facebook_hashtags?.length > 0 ? option.facebook_hashtags : form.facebook.hashtags
      };
    }
    if (selectedPlatforms.includes('instagram')) {
      newForm.instagram = {
        title: option.instagram_title || form.instagram.title,
        description: option.instagram_caption || form.instagram.description,
        keywords: option.instagram_keywords?.length > 0 ? option.instagram_keywords : form.instagram.keywords,
        hashtags: option.instagram_hashtags?.length > 0 ? option.instagram_hashtags : form.instagram.hashtags
      };
    }
    setForm(newForm);
    toast.success(`Option ${index + 1} applied to all platforms`);
  };

  const generateThumbnail = async () => {
    
    if (!apiKey) { toast.error('Please add your Obsidian AI API key first'); return; }

    const title = form.youtube.title || form.tiktok.title || form.facebook.title || form.instagram.title || videoFile?.name || 'Video';
    const niche = detectedNiche || 'General';

    setGeneratingThumbnail(true);
    setThumbnailConcepts(null);
    setGeneratedThumbnailUrl(null);
    setActivePlatformTab(null);
    setThumbnailFile(null);

    try {
      const res = await fetch('https://vantage-production-4475.up.railway.app/api/obsidian/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          
          
        },
        body: JSON.stringify({
          model: 'claude-opus-4-5',
          max_tokens: 1500,
          system: 'You are an expert YouTube thumbnail designer and CTR specialist. You ONLY help with thumbnails for content platforms.',
          messages: [{
            role: 'user',
            content: `Generate 3 thumbnail concepts for:
VIDEO TITLE: ${title}
NICHE: ${niche}
PLATFORMS: ${selectedPlatforms.join(', ')}

=== CONCEPT 1 ===
VISUAL: [description]
CTR_PSYCHOLOGY: [explanation]
TEXT_OVERLAY: [text]

=== CONCEPT 2 ===
VISUAL: [description]
CTR_PSYCHOLOGY: [explanation]
TEXT_OVERLAY: [text]

=== CONCEPT 3 ===
VISUAL: [description]
CTR_PSYCHOLOGY: [explanation]
TEXT_OVERLAY: [text]

=== BEST CONCEPT ===
SELECTED: [1, 2 or 3]
REASON: [why]
FLUX_PROMPT: [complete detailed image generation prompt for Flux or Midjourney]`
          }]
        })
      });

      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      const rawText = data.content[0].text;

      const concepts = [];
      const blocks = rawText.split(/=== CONCEPT \d+ ===/);
      blocks.shift();
      blocks.slice(0, 3).forEach(block => {
        const visual = block.match(/VISUAL:\s*([\s\S]+?)(?=CTR_PSYCHOLOGY:|$)/);
        const ctr = block.match(/CTR_PSYCHOLOGY:\s*([\s\S]+?)(?=TEXT_OVERLAY:|$)/);
        const text = block.match(/TEXT_OVERLAY:\s*(.+?)(?:\n|$)/);
        concepts.push({
          visual: visual ? visual[1].trim() : '',
          ctr_psychology: ctr ? ctr[1].trim() : '',
          text_overlay: text ? text[1].trim() : ''
        });
      });

      const fluxMatch = rawText.match(/FLUX_PROMPT:\s*([\s\S]+?)(?:\n===|$)/);
      const selectedConcept = rawText.match(/SELECTED:\s*(\d+)/);
      const reason = rawText.match(/REASON:\s*([\s\S]+?)(?=FLUX_PROMPT:|$)/);

      setThumbnailConcepts({
        concepts,
        selected: selectedConcept ? parseInt(selectedConcept[1]) : 1,
        reason: reason ? reason[1].trim() : '',
        flux_prompt: fluxMatch ? fluxMatch[1].trim() : ''
      });

      if (fluxMatch) {
        try {
          const muapiRes = await fetch('https://vantage-production-4475.up.railway.app/api/contentforge/generate-image', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              prompt: `Professional YouTube thumbnail, ${fluxMatch[1].trim()}, ultra high quality, 4K, vibrant colors, high contrast, 16:9`
            })
          });
          const muapiData = await muapiRes.json();
          if (muapiData.success && muapiData.data?.image_url) {
            setGeneratedThumbnailUrl(muapiData.data.image_url);
            toast.success('Thumbnail generated!');
          } else {
            toast.info('Concepts ready — add Muapi key for actual image');
          }
        } catch (e) {
          toast.info('Concepts ready — Muapi needed for image generation');
        }
      }
    } catch (error) {
      toast.error('Failed: ' + error.message);
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  const handleSubmit = async (isDraft = false) => {
    if (selectedPlatforms.length === 0) { toast.error('Please select at least one platform'); return; }
    if (!videoFile) { toast.error('Please upload a video file'); return; }
    if (selectedChannels.length === 0) { toast.error('Please select at least one channel'); return; }

    setSaving(true);
    try {
      const videoData = {
        title: form.youtube.title || form.tiktok.title || form.facebook.title || form.instagram.title || videoFile.name,
        description: form.youtube.description || form.tiktok.description || '',
        youtube_title: form.youtube.title,
        youtube_description: form.youtube.description,
        youtube_keywords: form.youtube.keywords?.join(', '),
        youtube_hashtags: form.youtube.hashtags?.join(' '),
        tiktok_title: form.tiktok.title,
        tiktok_description: form.tiktok.description,
        tiktok_keywords: form.tiktok.keywords?.join(', '),
        tiktok_hashtags: form.tiktok.hashtags?.join(' '),
        facebook_title: form.facebook.title,
        facebook_description: form.facebook.description,
        facebook_keywords: form.facebook.keywords?.join(', '),
        facebook_hashtags: form.facebook.hashtags?.join(' '),
        instagram_title: form.instagram.title,
        instagram_description: form.instagram.description,
        instagram_keywords: form.instagram.keywords?.join(', '),
        instagram_hashtags: form.instagram.hashtags?.join(' '),
        tags: [...(form.youtube.keywords || []), ...(form.tiktok.keywords || [])].join(', '),
        niche: detectedNiche,
        status: isDraft ? 'draft' : 'pending',
        publish_mode: publishMode,
        metadata_control: metadataControl,
        channel_ids: selectedChannels,
        channel_id: selectedChannels[0] || null,
        platforms: selectedPlatforms,
        selected_option_index: selectedOption,
        thumbnail_url: generatedThumbnailUrl || ''
      };

      videoData.file = videoFile;
      const currentState = {
        step, selectedPlatforms, videoFile, form,
        selectedChannels, publishMode, activePlatformTab
      };
      startUpload(videoFile.name, currentState);
      await uploadVideo(
        videoData,
        (percent) => updateProgress(percent),
        (xhr) => setXhr(xhr)
      );
      if (publishMode === 'instant_auto') {
        updateProgress(100);
        toast.info('⚡ Obsidian AI is generating metadata and publishing automatically...');
      }
      finishUpload(true);
      setSavedUploadState(null);
      toast.success(
        isDraft ? 'Draft saved' :
          publishMode === 'instant_auto' ? '⚡ Obsidian AI is publishing automatically' :
            publishMode === 'scheduled_auto' ? '⏰ Will publish at scheduled time' :
              '👤 Saved for manual review'
      );

      setStep(1);
      setSelectedPlatforms([]);
      setVideoFile(null);
      setVideoDuration(null);
      setForm(emptyForm);
      setSelectedChannels([]);
      setPublishMode('manual');
      setMetadataControl('user_choice');
      setAiOptions(null);
      setSelectedOption(null);
      setDetectedNiche('');
      setThumbnailConcepts(null);
      setGeneratedThumbnailUrl(null);
      setActivePlatformTab(null);
    } catch (error) {
      finishUpload(false);
      toast.error('Failed: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { number: 1, label: 'Platforms' },
    { number: 2, label: 'Upload' },
    { number: 3, label: 'Title & Description' },
    { number: 4, label: 'Thumbnail' },
    { number: 5, label: 'Publish' }
  ];

  const filteredChannels = channels.filter(ch =>
    selectedPlatforms.some(p =>
      p === ch.platform || (p === 'youtube_shorts' && ch.platform === 'youtube')
    )
  );

  const activePlatformCfg = platformConfig.find(p => p.id === activePlatformTab);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><span className="gradient-text">Upload Video</span></h1>
        <p>Upload once and publish to every platform simultaneously</p>
      </div>

      {/* Step Indicator */}
      <div style={{
        display: 'flex', alignItems: 'center', marginBottom: '32px',
        background: 'var(--bg-card)', borderRadius: '14px', padding: '16px 24px',
        border: '1px solid var(--border)', overflowX: 'auto', gap: '4px'
      }}>
        {steps.map((s, i) => (
          <React.Fragment key={s.number}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: '800', fontSize: '13px',
                background: step > s.number ? 'var(--primary)' :
                  step === s.number ? 'linear-gradient(135deg,#00c853,#1de9b6)' : 'var(--bg-dark)',
                color: step >= s.number ? 'white' : 'var(--text-secondary)',
                border: step < s.number ? '2px solid var(--border)' : 'none',
                flexShrink: 0, transition: 'all 0.3s ease',
                boxShadow: step === s.number ? '0 0 12px rgba(0,200,83,0.4)' : 'none'
              }}>
                {step > s.number ? <MdCheck /> : s.number}
              </div>
              <span style={{
                fontSize: '11px', fontWeight: step === s.number ? '700' : '500',
                color: step === s.number ? 'var(--primary)' : 'var(--text-secondary)',
                transition: 'all 0.3s ease', whiteSpace: 'nowrap'
              }}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: '2px', margin: '0 6px', minWidth: '16px',
                background: step > s.number ? 'var(--primary)' : 'var(--border)',
                transition: 'all 0.3s ease'
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 — Platforms */}
      {step === 1 && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
            Select Platforms
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '24px' }}>
            Choose where you want to publish — select multiple platforms
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px', marginBottom: '28px'
          }}>
            {platformConfig.map(p => (
              <PlatformTile
                key={p.id}
                platform={p}
                selected={selectedPlatforms.includes(p.id)}
                onClick={() => togglePlatform(p.id)}
              />
            ))}
          </div>
          {selectedPlatforms.length > 0 && (
            <div style={{
              display: 'flex', gap: '8px', flexWrap: 'wrap',
              marginBottom: '20px', padding: '12px 16px',
              background: 'rgba(0,200,83,0.06)', borderRadius: '10px',
              border: '1px solid rgba(0,200,83,0.2)'
            }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                Selected:
              </span>
              {selectedPlatforms.map(p => {
                const cfg = platformConfig.find(pc => pc.id === p);
                return (
                  <span key={p} style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '12px',
                    fontWeight: '700', background: cfg.bg, color: cfg.color,
                    border: '1px solid ' + cfg.border
                  }}>
                    {cfg.label} ✓
                  </span>
                );
              })}
            </div>
          )}
          {selectedPlatforms.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>
                How should Obsidian AI handle publishing?
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { id: 'manual', label: '👤 Manual Review', desc: 'Obsidian generates 5 options — you pick the best title and publish yourself' },
                  { id: 'scheduled_auto', label: '⏰ Scheduled Auto', desc: 'Obsidian picks the best option and publishes automatically at your scheduled time' },
                  { id: 'instant_auto', label: '⚡ Instant Auto', desc: 'Obsidian immediately picks and publishes after upload — zero human intervention' }
                ].map(mode => (
                  <div key={mode.id}
                    onClick={() => setPublishMode(mode.id)}
                    style={{
                      padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
                      background: publishMode === mode.id ? 'rgba(0,200,83,0.1)' : 'var(--bg-dark)',
                      border: '1px solid ' + (publishMode === mode.id ? 'rgba(0,200,83,0.4)' : 'var(--border)'),
                      transition: 'all 0.2s ease'
                    }}>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                      {mode.label}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {mode.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <ActionBtn className="btn btn-primary btn-lg"
            onClick={() => setStep(2)}
            disabled={selectedPlatforms.length === 0}
            style={{ width: '100%' }}>
            Continue <MdArrowForward />
          </ActionBtn>
        </div>
      )}

      {/* STEP 2 — Upload */}
      {step === 2 && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
            Upload Video
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '20px' }}>
            Select your channels and upload your video file
          </p>

          {/* Channel Selection */}
          <div className="card" style={{
            marginBottom: '20px',
            border: selectedChannels.length === 0 ? '2px solid rgba(239,68,68,0.3)' : '2px solid rgba(0,200,83,0.3)'
          }}>
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                Upload to Channels
                <span style={{
                  fontSize: '11px', background: 'rgba(239,68,68,0.15)',
                  color: '#ef4444', padding: '2px 8px', borderRadius: '8px', fontWeight: '700'
                }}>Required</span>
              </span>
              {selectedChannels.length > 0 && (
                <span className="badge badge-success">{selectedChannels.length} selected</span>
              )}
            </div>
            {filteredChannels.length === 0 ? (
              <div style={{
                padding: '20px', textAlign: 'center', fontSize: '13px',
                color: 'var(--text-secondary)', background: 'var(--bg-dark)',
                borderRadius: '10px', border: '1px solid var(--border)'
              }}>
                No channels found for selected platforms. Add channels in Platform Manager first.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filteredChannels.map(ch => {
                  const isSelected = selectedChannels.includes(ch.id.toString());
                  const platformIcon = {
                    youtube: <FaYoutube style={{ color: '#ff0000', fontSize: '16px' }} />,
                    facebook: <FaFacebook style={{ color: '#1877f2', fontSize: '16px' }} />,
                    tiktok: <FaTiktok style={{ color: '#e0e0e0', fontSize: '16px' }} />,
                    instagram: <FaInstagram style={{ color: '#e1306c', fontSize: '16px' }} />
                  }[ch.platform];
                  return (
                    <div key={ch.id} onClick={() => toggleChannel(ch.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
                        background: isSelected ? 'rgba(0,200,83,0.08)' : 'var(--bg-dark)',
                        border: '2px solid ' + (isSelected ? 'rgba(0,200,83,0.5)' : 'var(--border)'),
                        transition: 'all 0.2s ease',
                        transform: isSelected ? 'translateX(4px)' : 'translateX(0)'
                      }}>
                      {platformIcon}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>
                          {ch.name}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                          {ch.platform} {ch.has_oauth && '• OAuth Connected'}
                        </div>
                      </div>
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%',
                        border: '2px solid ' + (isSelected ? 'var(--primary)' : 'var(--border)'),
                        background: isSelected ? 'var(--primary)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s ease', flexShrink: 0
                      }}>
                        {isSelected && <MdCheck style={{ color: 'white', fontSize: '12px' }} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Video Upload */}
          {!videoFile ? (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]); }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(0,200,83,0.08)';
                e.currentTarget.style.transform = 'scale(1.01)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(0,200,83,0.04)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              style={{
                border: '2px dashed var(--primary)', borderRadius: '16px',
                padding: '60px 40px', textAlign: 'center', cursor: 'pointer',
                background: 'rgba(0,200,83,0.04)', marginBottom: '20px',
                transition: 'all 0.3s ease'
              }}>
              <MdCloudUpload style={{ fontSize: '64px', color: 'var(--primary)', opacity: 0.5, marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
                Drop your video here
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '8px' }}>
                or click to browse files
              </p>
              <p style={{ color: 'var(--text-light)', fontSize: '12px' }}>MP4, MOV, AVI, MKV and more</p>
              {selectedPlatforms.includes('youtube_shorts') && (
                <div style={{
                  marginTop: '16px', padding: '8px 16px', borderRadius: '8px',
                  background: 'rgba(255,68,68,0.1)', border: '1px solid rgba(255,68,68,0.3)',
                  fontSize: '12px', color: '#ff4444', fontWeight: '600', display: 'inline-block'
                }}>⚠️ YouTube Shorts — maximum 60 seconds</div>
              )}
              <input ref={fileInputRef} type="file" accept="video/*" style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files[0])} />
            </div>
          ) : (
            <div className="card" style={{ marginBottom: '20px', border: '1px solid rgba(0,200,83,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <MdVideoLibrary style={{ fontSize: '48px', color: 'var(--primary)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {videoFile.name}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {(videoFile.size / 1024 / 1024).toFixed(2)} MB
                    {videoDuration && ` • ${Math.round(videoDuration)} seconds`}
                  </div>
                  {selectedPlatforms.includes('youtube_shorts') && videoDuration > 60 && (
                    <div style={{
                      marginTop: '8px', padding: '6px 10px', borderRadius: '6px',
                      background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                      fontSize: '12px', color: '#ef4444', fontWeight: '600'
                    }}>❌ Exceeds 60 second Shorts limit</div>
                  )}
                  {selectedPlatforms.includes('youtube_shorts') && videoDuration && videoDuration <= 60 && (
                    <div style={{
                      marginTop: '8px', padding: '6px 10px', borderRadius: '6px',
                      background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.2)',
                      fontSize: '12px', color: 'var(--primary)', fontWeight: '600'
                    }}>✅ Perfect for YouTube Shorts</div>
                  )}
                </div>
                <button className="btn btn-secondary btn-sm"
                  onClick={() => { setVideoFile(null); setVideoDuration(null); }}>
                  <MdClose />
                </button>
              </div>
            </div>
          )}

          {/* Thumbnail Upload */}
          <div className="card" style={{ marginBottom: '20px', border: '1px solid var(--border)' }}>
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MdImage style={{ color: 'var(--primary)' }} /> Thumbnail (Optional)
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Skip to generate with AI in next steps</span>
            </div>
            {!thumbnailFile ? (
              <div onClick={() => thumbnailInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--border)', borderRadius: '12px',
                  padding: '24px', textAlign: 'center', cursor: 'pointer',
                  background: 'var(--bg-dark)', transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <MdImage style={{ fontSize: '32px', color: 'var(--text-secondary)', marginBottom: '8px' }} />
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Click to upload thumbnail image</p>
                <p style={{ fontSize: '11px', color: 'var(--text-light)' }}>JPG, PNG — 1280x720 recommended</p>
                <input ref={thumbnailInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e) => setThumbnailFile(e.target.files[0])} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(0,200,83,0.06)', borderRadius: '10px' }}>
                <MdImage style={{ fontSize: '32px', color: 'var(--primary)' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)' }}>{thumbnailFile.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{(thumbnailFile.size / 1024).toFixed(0)} KB</div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setThumbnailFile(null)}>
                  <MdClose />
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <ActionBtn className="btn btn-secondary" onClick={() => setStep(1)} style={{ flex: 1 }}>
              <MdArrowBack /> Back
            </ActionBtn>
            <ActionBtn className="btn btn-primary" onClick={() => {
              if (selectedChannels.length === 0) { toast.error('Please select at least one channel'); return; }
              if (!videoFile) { toast.error('Please upload a video file'); return; }
              if (publishMode === 'instant_auto') {
                setStep(5);
              } else {
                setStep(3);
              }
            }} style={{ flex: 2 }}>
              Continue <MdArrowForward />
            </ActionBtn>
          </div>
        </div>
      )}

      {/* STEP 3 — Title and Description */}
      {step === 3 && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
            Title and Description
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '20px' }}>
            Fill in manually or let Obsidian AI generate platform specific content for you
          </p>

          {/* Input Mode Toggle */}
          <div style={{
            display: 'flex', gap: '10px', marginBottom: '20px',
            background: 'var(--bg-dark)', borderRadius: '12px', padding: '6px'
          }}>
            {[
              { id: 'manual', label: '✋ Fill Manually', icon: <MdEdit /> },
              { id: 'ai', label: '🤖 AI Suggestions', icon: <MdSmartToy /> }
            ].map(mode => (
              <button key={mode.id}
                onClick={() => setInputMode(mode.id)}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: '8px', cursor: 'pointer',
                  background: inputMode === mode.id ? 'var(--primary-gradient)' : 'transparent',
                  border: 'none', color: inputMode === mode.id ? 'white' : 'var(--text-secondary)',
                  fontWeight: '700', fontSize: '13px', transition: 'all 0.2s ease',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  fontFamily: 'Inter, sans-serif'
                }}>
                {mode.label}
              </button>
            ))}
          </div>

          {/* MANUAL MODE */}
          {inputMode === 'manual' && (
            <div>
              {/* Platform Tabs */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto' }}>
                {selectedPlatforms.map(p => {
                  const cfg = platformConfig.find(pc => pc.id === p);
                  const isActive = activePlatformTab === p;
                  const hasContent = form[p]?.title || form[p]?.description;
                  return (
                    <button key={p}
                      onClick={() => setActivePlatformTab(p)}
                      style={{
                        padding: '8px 16px', borderRadius: '10px', cursor: 'pointer',
                        background: isActive ? cfg.bg : 'var(--bg-dark)',
                        border: '2px solid ' + (isActive ? cfg.color : 'var(--border)'),
                        color: isActive ? cfg.color : 'var(--text-secondary)',
                        fontWeight: '700', fontSize: '12px', transition: 'all 0.2s ease',
                        display: 'flex', alignItems: 'center', gap: '6px',
                        whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif',
                        flexShrink: 0
                      }}>
                      {cfg?.icon && React.cloneElement(cfg.icon, { style: { fontSize: '14px', color: isActive ? cfg.color : 'var(--text-secondary)' } })}
                      {cfg?.label}
                      {hasContent && <span style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        background: cfg.color, display: 'inline-block'
                      }} />}
                    </button>
                  );
                })}
              </div>

              {/* Active Platform Fields */}
              {activePlatformTab && (
                <div className="card" style={{ border: '2px solid ' + (activePlatformCfg?.border || 'var(--border)') }}>
                  <div className="card-header">
                    <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {activePlatformCfg?.icon && React.cloneElement(activePlatformCfg.icon, { style: { fontSize: '18px', color: activePlatformCfg.color } })}
                      {activePlatformCfg?.label} — Title and Description
                    </span>
                  </div>

                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px', padding: '8px 12px', background: 'var(--bg-dark)', borderRadius: '8px' }}>
                    {activePlatformTab === 'youtube' && '📺 YouTube rewards SEO titles, keyword rich descriptions with timestamps, 5-10 niche hashtags'}
                    {activePlatformTab === 'youtube_shorts' && '⚡ Shorts rewards punchy titles, minimal descriptions, trending hashtags'}
                    {activePlatformTab === 'tiktok' && '🎵 TikTok rewards trendy titles, short viral captions, For You Page hashtags'}
                    {activePlatformTab === 'facebook' && '👥 Facebook rewards emotional titles, community stories, minimal hashtags'}
                    {activePlatformTab === 'instagram' && '📸 Instagram rewards aesthetic titles, storytelling captions, 20-30 niche hashtags'}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Title</label>
                    <input className="form-control"
                      placeholder={`${activePlatformCfg?.label} title — leave blank for Obsidian AI`}
                      value={form[activePlatformTab]?.title || ''}
                      onChange={(e) => updatePlatformField(activePlatformTab, 'title', e.target.value)} />
                    {(activePlatformTab === 'youtube' || activePlatformTab === 'youtube_shorts') && (
                      <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                        {(form[activePlatformTab]?.title || '').length}/100 characters
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {activePlatformTab === 'tiktok' || activePlatformTab === 'instagram' ? 'Caption' : 'Description'}
                    </label>
                    <textarea className="form-control"
                      placeholder={
                        activePlatformTab === 'tiktok' ? 'Short punchy caption — leave blank for Obsidian AI' :
                          activePlatformTab === 'instagram' ? 'Aesthetic storytelling caption — leave blank for Obsidian AI' :
                            activePlatformTab === 'facebook' ? 'Community focused description — leave blank for Obsidian AI' :
                              'Full description with timestamps — leave blank for Obsidian AI'
                      }
                      rows={activePlatformTab === 'youtube' ? 5 : 3}
                      value={form[activePlatformTab]?.description || ''}
                      onChange={(e) => updatePlatformField(activePlatformTab, 'description', e.target.value)} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">🔑 Keywords for {activePlatformCfg?.label}</label>
                    <TagInput
                      tags={form[activePlatformTab]?.keywords || []}
                      onAdd={(val) => addTag(activePlatformTab, 'keywords', val)}
                      onRemove={(val) => removeTag(activePlatformTab, 'keywords', val)}
                      placeholder={`Add ${activePlatformCfg?.label} keyword — leave empty for AI`}
                      color="var(--primary)"
                      bg="rgba(0,200,83,0.1)"
                      border="rgba(0,200,83,0.3)"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label"># Hashtags for {activePlatformCfg?.label}</label>
                    <TagInput
                      tags={form[activePlatformTab]?.hashtags || []}
                      onAdd={(val) => addTag(activePlatformTab, 'hashtags', val)}
                      onRemove={(val) => removeTag(activePlatformTab, 'hashtags', val)}
                      placeholder={`Add #hashtag for ${activePlatformCfg?.label} — leave empty for AI`}
                      color="#00bcd4"
                      bg="rgba(0,188,212,0.1)"
                      border="rgba(0,188,212,0.3)"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI SUGGESTIONS MODE */}
          {inputMode === 'ai' && (
            <div>
              {/* Metadata Control */}
              <div className="card" style={{ marginBottom: '16px' }}>
                <div className="card-header">
                  <span className="card-title">Who controls the selection?</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { id: 'user_choice', label: '✋ I will pick', desc: 'You select one of the 5 AI generated options below' },
                    { id: 'obsidian_decides', label: '🤖 Let Obsidian Decide', desc: 'Obsidian automatically picks and applies the best option for your niche' }
                  ].map(ctrl => (
                    <div key={ctrl.id}
                      onClick={() => {
                        setMetadataControl(ctrl.id);
                        if (ctrl.id === 'obsidian_decides' && aiOptions) {
                          applyOption(aiOptions.auto_selected - 1, aiOptions.options);
                        }
                      }}
                      style={{
                        padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                        background: metadataControl === ctrl.id ? 'rgba(0,200,83,0.08)' : 'var(--bg-dark)',
                        border: '1px solid ' + (metadataControl === ctrl.id ? 'rgba(0,200,83,0.4)' : 'var(--border)'),
                        transition: 'all 0.2s ease'
                      }}>
                      <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {ctrl.label} {metadataControl === ctrl.id && <MdCheck style={{ color: 'var(--primary)' }} />}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{ctrl.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <ActionBtn className="btn btn-primary"
                onClick={generateAIOptions}
                disabled={generatingAI}
                style={{ width: '100%', marginBottom: '16px' }}>
                {generatingAI
                  ? <><div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> Obsidian AI is generating 5 platform specific options...</>
                  : <><MdAutoAwesome /> {aiOptions ? 'Regenerate 5 Options' : 'Generate 5 Platform Specific Options'}</>}
              </ActionBtn>

              {/* AI Recommendation Banner */}
              {aiOptions && (
                <div>
                  <div style={{
                    background: 'rgba(0,200,83,0.06)', border: '1px solid rgba(0,200,83,0.2)',
                    borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
                    fontSize: '13px', color: 'var(--text-secondary)'
                  }}>
                    <strong style={{ color: 'var(--primary)' }}>
                      Obsidian recommends Option {aiOptions.auto_selected}
                    </strong> — {aiOptions.auto_selection_reason}
                    {detectedNiche && (
                      <span style={{ marginLeft: '8px', color: 'var(--primary)', fontWeight: '600' }}>
                        | Niche: {detectedNiche}
                      </span>
                    )}
                    {metadataControl === 'user_choice' && selectedOption !== null && (
                      <div style={{ marginTop: '6px', color: '#f59e0b', fontWeight: '600', fontSize: '12px' }}>
                        👤 You selected Option {selectedOption + 1} — applied to all platforms
                      </div>
                    )}
                    {metadataControl === 'obsidian_decides' && selectedOption !== null && (
                      <div style={{ marginTop: '6px', color: 'var(--primary)', fontWeight: '600', fontSize: '12px' }}>
                        🤖 Obsidian selected Option {selectedOption + 1} and applied it automatically
                      </div>
                    )}
                  </div>

                  {/* 5 Options */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {aiOptions.options.map((option, index) => (
                      <div key={index}
                        onClick={() => metadataControl === 'user_choice' && applyOption(index, aiOptions.options)}
                        style={{
                          borderRadius: '14px',
                          cursor: metadataControl === 'user_choice' ? 'pointer' : 'default',
                          background: selectedOption === index ? 'rgba(0,200,83,0.06)' : 'var(--bg-dark)',
                          border: '2px solid ' + (
                            selectedOption === index ? 'rgba(0,200,83,0.5)' :
                              aiOptions.auto_selected - 1 === index ? 'rgba(0,200,83,0.2)' : 'var(--border)'
                          ),
                          transition: 'all 0.2s ease', overflow: 'hidden'
                        }}>

                        {/* Option Header */}
                        <div style={{
                          padding: '12px 16px',
                          background: selectedOption === index ? 'rgba(0,200,83,0.1)' : 'var(--bg-card)',
                          borderBottom: '1px solid var(--border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          flexWrap: 'wrap', gap: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '50%',
                              background: selectedOption === index ? 'var(--primary)' : 'var(--bg-dark)',
                              border: '2px solid ' + (selectedOption === index ? 'var(--primary)' : 'var(--border)'),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '12px', fontWeight: '800',
                              color: selectedOption === index ? 'white' : 'var(--text-secondary)', flexShrink: 0
                            }}>
                              {selectedOption === index ? <MdCheck /> : index + 1}
                            </div>
                            <span style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)' }}>
                              Option {index + 1}
                            </span>
                            {aiOptions.auto_selected - 1 === index && (
                              <span style={{
                                background: 'var(--primary)', color: 'white',
                                fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px'
                              }}>AI RECOMMENDED</span>
                            )}
                            {selectedOption === index && (
                              <span style={{
                                background: metadataControl === 'obsidian_decides' ? 'var(--primary)' : '#f59e0b',
                                color: 'white', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px'
                              }}>
                                {metadataControl === 'obsidian_decides' ? 'OBSIDIAN PICK' : 'YOUR CHOICE'}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={{
                              fontSize: '11px', padding: '3px 10px', borderRadius: '8px',
                              background: 'rgba(0,200,83,0.1)', color: 'var(--primary)', fontWeight: '700'
                            }}>CTR {option.ctr_score}</span>
                            <span style={{
                              fontSize: '11px', padding: '3px 10px', borderRadius: '8px',
                              background: 'rgba(0,188,212,0.1)', color: '#00bcd4', fontWeight: '700'
                            }}>SEO {option.seo_score}</span>
                            <span style={{
                              fontSize: '11px', padding: '3px 10px', borderRadius: '8px',
                              background: 'var(--bg-card)', color: 'var(--text-secondary)'
                            }}>{option.best_for}</span>
                          </div>
                        </div>

                        {/* Platform Content */}
                        <div style={{ padding: '16px' }}>

                          {/* YouTube */}
                          {(selectedPlatforms.includes('youtube') || selectedPlatforms.includes('youtube_shorts')) && (
                            <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                                <FaYoutube style={{ color: '#ff0000', fontSize: '14px' }} />
                                <span style={{ fontWeight: '700', fontSize: '12px', color: '#ff0000' }}>YOUTUBE</span>
                              </div>
                              <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                                {option.youtube_title}
                              </div>
                              <div style={{
                                fontSize: '12px', color: 'var(--text-secondary)', background: 'var(--bg-card)',
                                borderRadius: '8px', padding: '10px 12px', border: '1px solid var(--border)',
                                whiteSpace: 'pre-wrap', lineHeight: '1.7', maxHeight: '140px', overflowY: 'auto',
                                marginBottom: '8px'
                              }}>
                                {option.youtube_description || 'Description will be generated'}
                              </div>
                              {option.youtube_keywords?.length > 0 && (
                                <div style={{ marginBottom: '6px' }}>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Keywords: </span>
                                  <span style={{ fontSize: '11px', color: 'var(--primary)' }}>
                                    {option.youtube_keywords.join(', ')}
                                  </span>
                                </div>
                              )}
                              {option.youtube_hashtags?.length > 0 && (
                                <div>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Hashtags: </span>
                                  <span style={{ fontSize: '11px', color: '#00bcd4' }}>
                                    {option.youtube_hashtags.join(' ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* TikTok */}
                          {selectedPlatforms.includes('tiktok') && (
                            <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                                <FaTiktok style={{ color: '#e0e0e0', fontSize: '14px' }} />
                                <span style={{ fontWeight: '700', fontSize: '12px', color: '#e0e0e0' }}>TIKTOK</span>
                              </div>
                              <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                                {option.tiktok_title}
                              </div>
                              <div style={{
                                fontSize: '12px', color: 'var(--text-secondary)', background: 'var(--bg-card)',
                                borderRadius: '8px', padding: '10px 12px', border: '1px solid var(--border)',
                                whiteSpace: 'pre-wrap', lineHeight: '1.7', marginBottom: '8px'
                              }}>
                                {option.tiktok_caption || 'Caption will be generated'}
                              </div>
                              {option.tiktok_keywords?.length > 0 && (
                                <div style={{ marginBottom: '6px' }}>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Keywords: </span>
                                  <span style={{ fontSize: '11px', color: 'var(--primary)' }}>
                                    {option.tiktok_keywords.join(', ')}
                                  </span>
                                </div>
                              )}
                              {option.tiktok_hashtags?.length > 0 && (
                                <div>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Hashtags: </span>
                                  <span style={{ fontSize: '11px', color: '#00bcd4' }}>
                                    {option.tiktok_hashtags.join(' ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Facebook */}
                          {selectedPlatforms.includes('facebook') && (
                            <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                                <FaFacebook style={{ color: '#1877f2', fontSize: '14px' }} />
                                <span style={{ fontWeight: '700', fontSize: '12px', color: '#1877f2' }}>FACEBOOK</span>
                              </div>
                              <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                                {option.facebook_title}
                              </div>
                              <div style={{
                                fontSize: '12px', color: 'var(--text-secondary)', background: 'var(--bg-card)',
                                borderRadius: '8px', padding: '10px 12px', border: '1px solid var(--border)',
                                whiteSpace: 'pre-wrap', lineHeight: '1.7', marginBottom: '8px'
                              }}>
                                {option.facebook_description || 'Description will be generated'}
                              </div>
                              {option.facebook_keywords?.length > 0 && (
                                <div style={{ marginBottom: '6px' }}>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Keywords: </span>
                                  <span style={{ fontSize: '11px', color: 'var(--primary)' }}>
                                    {option.facebook_keywords.join(', ')}
                                  </span>
                                </div>
                              )}
                              {option.facebook_hashtags?.length > 0 && (
                                <div>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Hashtags: </span>
                                  <span style={{ fontSize: '11px', color: '#00bcd4' }}>
                                    {option.facebook_hashtags.join(' ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Instagram */}
                          {selectedPlatforms.includes('instagram') && (
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                                <FaInstagram style={{ color: '#e1306c', fontSize: '14px' }} />
                                <span style={{ fontWeight: '700', fontSize: '12px', color: '#e1306c' }}>INSTAGRAM</span>
                              </div>
                              <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '8px' }}>
                                {option.instagram_title}
                              </div>
                              <div style={{
                                fontSize: '12px', color: 'var(--text-secondary)', background: 'var(--bg-card)',
                                borderRadius: '8px', padding: '10px 12px', border: '1px solid var(--border)',
                                whiteSpace: 'pre-wrap', lineHeight: '1.7',
                                maxHeight: '160px', overflowY: 'auto', marginBottom: '8px'
                              }}>
                                {option.instagram_caption || 'Caption will be generated'}
                              </div>
                              {option.instagram_keywords?.length > 0 && (
                                <div style={{ marginBottom: '6px' }}>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Keywords: </span>
                                  <span style={{ fontSize: '11px', color: 'var(--primary)' }}>
                                    {option.instagram_keywords.join(', ')}
                                  </span>
                                </div>
                              )}
                              {option.instagram_hashtags?.length > 0 && (
                                <div>
                                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '600' }}>Hashtags: </span>
                                  <span style={{ fontSize: '11px', color: '#00bcd4', lineHeight: '1.8' }}>
                                    {option.instagram_hashtags.join(' ')}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <ActionBtn className="btn btn-secondary" onClick={() => setStep(2)} style={{ flex: 1 }}>
              <MdArrowBack /> Back
            </ActionBtn>
            <ActionBtn className="btn btn-primary" onClick={() => thumbnailFile ? setStep(5) : setStep(4)} style={{ flex: 2 }}>
              Continue <MdArrowForward />
            </ActionBtn>
          </div>
        </div>
      )}

      {/* STEP 4 — Thumbnail */}
      {step === 4 && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
            Generate Thumbnail
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '20px' }}>
            Let Obsidian AI create a high CTR thumbnail — or skip this step
          </p>

          <div className="card" style={{ marginBottom: '20px', border: '1px solid rgba(0,200,83,0.3)' }}>
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MdImage style={{ color: 'var(--primary)' }} /> Thumbnail Studio
              </span>
              <span className="ai-badge">AI Powered</span>
            </div>
            <div style={{
              background: 'rgba(0,200,83,0.06)', borderRadius: '10px',
              padding: '12px 16px', marginBottom: '16px',
              fontSize: '13px', color: 'var(--text-secondary)'
            }}>
              Obsidian AI generates 3 thumbnail concepts with CTR psychology then creates the best one as an actual image using Muapi. Download and use it directly.
              {detectedNiche && <span style={{ color: 'var(--primary)', fontWeight: '600', marginLeft: '8px' }}>Niche: {detectedNiche}</span>}
            </div>

            <ActionBtn className="btn btn-primary"
              onClick={generateThumbnail}
              disabled={generatingThumbnail}
              style={{ width: '100%', marginBottom: '16px' }}>
              {generatingThumbnail
                ? <><div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> Generating thumbnail...</>
                : <><MdImage /> Generate Thumbnail with Obsidian AI</>}
            </ActionBtn>

            {thumbnailConcepts && (
              <div>
                <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)', marginBottom: '10px' }}>
                  3 Thumbnail Concepts
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                  {thumbnailConcepts.concepts.map((concept, index) => (
                    <div key={index} style={{
                      padding: '14px 16px', borderRadius: '12px',
                      background: thumbnailConcepts.selected - 1 === index ? 'rgba(0,200,83,0.08)' : 'var(--bg-dark)',
                      border: '1px solid ' + (thumbnailConcepts.selected - 1 === index ? 'rgba(0,200,83,0.4)' : 'var(--border)')
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text-primary)' }}>
                          Concept {index + 1}
                        </span>
                        {thumbnailConcepts.selected - 1 === index && (
                          <span style={{
                            background: 'var(--primary)', color: 'white',
                            fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px'
                          }}>AI SELECTED</span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        <strong style={{ color: 'var(--text-primary)' }}>Visual:</strong> {concept.visual}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                        <strong style={{ color: 'var(--text-primary)' }}>CTR:</strong> {concept.ctr_psychology}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--primary)', fontWeight: '600' }}>
                        Text: {concept.text_overlay}
                      </div>
                    </div>
                  ))}
                </div>

                {generatedThumbnailUrl ? (
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--primary)', marginBottom: '8px' }}>
                      ✅ AI Generated Thumbnail
                    </div>
                    <img src={generatedThumbnailUrl} alt="Generated thumbnail"
                      style={{ width: '100%', borderRadius: '10px', border: '1px solid var(--border)', marginBottom: '10px' }} />
                    <ActionBtn className="btn btn-secondary"
                      onClick={() => window.open(generatedThumbnailUrl, '_blank')}
                      style={{ width: '100%' }}>
                      <MdDownload /> Download Thumbnail
                    </ActionBtn>
                  </div>
                ) : (
                  <div style={{
                    padding: '12px 16px', borderRadius: '10px',
                    background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
                    fontSize: '12px', color: '#f59e0b'
                  }}>
                    💡 Concepts ready. Add Muapi API key to generate actual image.
                    {thumbnailConcepts.flux_prompt && (
                      <div style={{ marginTop: '8px' }}>
                        <strong>Use this prompt in Midjourney or Flux:</strong>
                        <div style={{
                          marginTop: '6px', padding: '8px 10px', background: 'var(--bg-dark)',
                          borderRadius: '6px', fontFamily: 'monospace', fontSize: '11px',
                          color: 'var(--text-secondary)', lineHeight: '1.5'
                        }}>
                          {thumbnailConcepts.flux_prompt}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <ActionBtn className="btn btn-secondary" onClick={() => setStep(3)} style={{ flex: 1 }}>
              <MdArrowBack /> Back
            </ActionBtn>
            <ActionBtn className="btn btn-secondary" onClick={() => setStep(5)} style={{ flex: 1 }}>
              Skip
            </ActionBtn>
            <ActionBtn className="btn btn-primary" onClick={() => setStep(5)} style={{ flex: 2 }}>
              Continue <MdArrowForward />
            </ActionBtn>
          </div>
        </div>
      )}

      {/* STEP 5 — Review and Publish */}
      {step === 5 && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: 'var(--text-primary)' }}>
            Review and Publish
          </h2>

          {/* Summary */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Upload Summary</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '110px' }}>Video:</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{videoFile?.name}</span>
              </div>
              {videoDuration && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ color: 'var(--text-secondary)', minWidth: '110px' }}>Duration:</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{Math.round(videoDuration)}s</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '110px' }}>Channels:</span>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {selectedChannels.map(id => {
                    const ch = channels.find(c => c.id.toString() === id);
                    return ch ? <span key={id} className="badge badge-success" style={{ fontSize: '11px' }}>{ch.name}</span> : null;
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--text-secondary)', minWidth: '110px' }}>Platforms:</span>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {selectedPlatforms.map(p => {
                    const cfg = platformConfig.find(pc => pc.id === p);
                    return <span key={p} style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                      fontWeight: '600', background: cfg.bg, color: cfg.color, border: '1px solid ' + cfg.border
                    }}>{cfg.label}</span>;
                  })}
                </div>
              </div>
              {detectedNiche && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ color: 'var(--text-secondary)', minWidth: '110px' }}>Niche:</span>
                  <span style={{ color: 'var(--primary)', fontWeight: '600' }}>{detectedNiche}</span>
                </div>
              )}
              {selectedOption !== null && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ color: 'var(--text-secondary)', minWidth: '110px' }}>AI Option:</span>
                  <span style={{ color: 'var(--primary)', fontWeight: '600' }}>Option {selectedOption + 1} applied</span>
                </div>
              )}
              {generatedThumbnailUrl && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ color: 'var(--text-secondary)', minWidth: '110px' }}>Thumbnail:</span>
                  <span style={{ color: 'var(--primary)', fontWeight: '600' }}>✅ AI Generated</span>
                </div>
              )}

              {/* Platform content preview */}
              {selectedPlatforms.map(p => {
                const cfg = platformConfig.find(pc => pc.id === p);
                const data = form[p];
                if (!data?.title && !data?.description) return null;
                return (
                  <div key={p} style={{
                    padding: '10px 14px', borderRadius: '10px',
                    background: cfg.bg, border: '1px solid ' + cfg.border
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      {React.cloneElement(cfg.icon, { style: { fontSize: '13px', color: cfg.color } })}
                      <span style={{ fontWeight: '700', fontSize: '12px', color: cfg.color }}>{cfg.label}</span>
                    </div>
                    {data.title && <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>{data.title}</div>}
                    {data.keywords?.length > 0 && (
                      <div style={{ fontSize: '11px', color: 'var(--primary)' }}>
                        Keywords: {data.keywords.join(', ')}
                      </div>
                    )}
                    {data.hashtags?.length > 0 && (
                      <div style={{ fontSize: '11px', color: '#00bcd4' }}>
                        {data.hashtags.join(' ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Publish Mode */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Publish Mode</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { id: 'manual', label: '👤 Manual Review', desc: 'You publish yourself whenever you are ready' },
                { id: 'scheduled_auto', label: '⏰ Scheduled Auto', desc: 'Obsidian publishes automatically at your scheduled time' },
                { id: 'instant_auto', label: '⚡ Instant Auto', desc: 'Obsidian publishes immediately after upload — zero human intervention' }
              ].map(mode => (
                <div key={mode.id}
                  onClick={() => setPublishMode(mode.id)}
                  style={{
                    padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
                    background: publishMode === mode.id ? 'rgba(0,200,83,0.08)' : 'var(--bg-dark)',
                    border: '2px solid ' + (publishMode === mode.id ? 'rgba(0,200,83,0.4)' : 'var(--border)'),
                    transition: 'all 0.2s ease',
                    transform: publishMode === mode.id ? 'translateX(4px)' : 'translateX(0)'
                  }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {mode.label} {publishMode === mode.id && <MdCheck style={{ color: 'var(--primary)' }} />}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{mode.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <ActionBtn className="btn btn-secondary" onClick={() => setStep(4)} style={{ flex: 1 }}>
              <MdArrowBack /> Back
            </ActionBtn>
            <ActionBtn className="btn btn-secondary" onClick={() => handleSubmit(true)}
              disabled={saving} style={{ flex: 1 }}>
              Save Draft
            </ActionBtn>
            <ActionBtn className="btn btn-primary" onClick={() => handleSubmit(false)}
              disabled={saving} style={{ flex: 2 }}>
              {saving
                ? <><div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} /> Saving...</>
                : publishMode === 'instant_auto' ? '⚡ Upload and Auto Publish'
                  : publishMode === 'scheduled_auto' ? '⏰ Upload and Schedule'
                    : '👤 Upload for Manual Review'}
            </ActionBtn>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoUpload;